import React, { useState, useMemo } from 'react'
import {
  Stack,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useSWR, { SWRResponse } from 'swr'
import axiosInstance from '../../../utils/axiosInstance'
import EnhancedTableHeader from './EnhancedTableHeader'
import EnhancedTableToolbar from './EnhancedTableToolbar'
import ProductRow from './ProductRow'
import CreateProductDialog, { ProductFormData } from './components/CreateProductDialog'
import EditProductDialog, { EditProductFormData } from './components/EditProductDialog'
import { Product, ProductStatusFilter } from '../types'

const headCells = [
  { id: 'displayId', label: 'Product ID' },
  { id: 'name', label: 'Product' },
  { id: 'supplier', label: 'Supplier' },
  { id: 'price', label: 'Price' },
  { id: 'runtime', label: 'Duration' },
  { id: 'condition', label: 'Purchase Condition' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions' },
]

export default function ProductTable() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchQuery, setSearchQuery] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState<ProductStatusFilter>('all')

  const editProductId = searchParams.get('id')
  const isCreateDialogOpen = searchParams.get('create') !== null

  // Fetch product list
  const {
    data: productsData,
    isLoading: loading,
    error,
    mutate,
  }: SWRResponse = useSWR('/products', (url) => axiosInstance.get(url))
  const products: Product[] = useMemo(() => productsData?.data?.products ?? [], [productsData])

  // Fetch single product when editing
  const { data: editProductData }: SWRResponse = useSWR(
    editProductId ? `/products/${editProductId}` : null,
    (url) => axiosInstance.get(url)
  )
  const editProduct: Product | null = editProductData?.data ?? null
  const isEditDialogOpen = editProductId !== null && editProduct !== null

  // Only non-bundle products can be added to a bundle
  const singleProducts = useMemo(() => products.filter((p) => !p.isBundle), [products])

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesSearch =
        searchQuery === '' || product.name.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesSupplier = supplierFilter === 'all' || product.supplier === supplierFilter
      const matchesStatus = statusFilter === 'all' || product.status === statusFilter
      return matchesSearch && matchesSupplier && matchesStatus
    })
  }, [products, searchQuery, supplierFilter, statusFilter])

  const paginatedProducts = useMemo(() => {
    const start = page * rowsPerPage
    return filteredProducts.slice(start, start + rowsPerPage)
  }, [filteredProducts, page, rowsPerPage])

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleEdit = (product: Product) => navigate({ search: `id=${product.id}` })
  const handleCloseEditDialog = () => navigate({ search: '' })
  const handleAddProduct = () => navigate({ search: 'create' })
  const handleCloseCreateDialog = () => navigate({ search: '' })

  const handleCreateProduct = async (data: ProductFormData) => {
    await axiosInstance.post('/products', {
      name: data.name,
      supplier: data.supplier || undefined,
      price: data.price || undefined,
      runtime: data.duration || undefined,
      status: data.status,
      pricingType: data.purchaseConditionType,
      condition: data.purchaseConditionValue ? parseFloat(data.purchaseConditionValue) : undefined,
      isBundle: data.productType === 'bundle',
      bundleItemIds: data.productType === 'bundle' ? data.selectedProducts : undefined,
    })
    mutate()
    handleCloseCreateDialog()
  }

  const handleUpdateProduct = async (data: EditProductFormData) => {
    if (!editProductId) return
    await axiosInstance.post(`/products/${editProductId}`, {
      name: data.name,
      supplier: data.supplier || undefined,
      price: data.price || undefined,
      runtime: data.duration || undefined,
      status: data.status,
      pricingType: data.purchaseConditionType,
      condition: data.purchaseConditionValue ? parseFloat(data.purchaseConditionValue) : undefined,
      bundleItemIds: data.selectedProducts,
    })
    mutate()
    handleCloseEditDialog()
  }

  const handleDelete = async (product: Product) => {
    await axiosInstance.delete(`/products/${product.id}`)
    mutate()
  }

  return (
    <Stack p={2} borderRadius={4} spacing={4} sx={{ background: '#fff' }}>
      <EnhancedTableToolbar
        totalCount={filteredProducts.length}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        supplierFilter={supplierFilter}
        onSupplierChange={setSupplierFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        onAddProduct={handleAddProduct}
      />

      {loading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress />
        </Stack>
      ) : error ? (
        <Alert severity="error">Failed to load products</Alert>
      ) : (
        <TableContainer>
          <Table sx={{ minWidth: 1000 }} aria-labelledby="tableTitle">
            <EnhancedTableHeader headCells={headCells} />
            <TableBody>
              {paginatedProducts.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredProducts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rows per page:"
      />

      <CreateProductDialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        onCreate={handleCreateProduct}
        availableProducts={singleProducts}
      />

      <EditProductDialog
        open={isEditDialogOpen}
        product={editProduct}
        onClose={handleCloseEditDialog}
        onUpdate={handleUpdateProduct}
        availableProducts={singleProducts}
      />
    </Stack>
  )
}
